import React from "react";
import {
  Button,
  Typography,
  Container,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import Navbar from "../../../../globals/components/Navbar";
import { LawFirmFuzzyMatchDialogBound } from "../../../../features/bonds/shared/components/LawFirmFuzzyMatchDialog";

import useCreateLawFirm from "./hooks/useCreateLawFirm";
import LawFirmDetailsStep from "./components/LawFirmDetailsStep";
import LocationsStep from "./components/LocationsStep";
import UsersStep from "./components/UsersStep";

function CreateLawFirm() {
  const {
    activeStep,
    steps,
    loading,
    lawFirmData,
    handleLawFirmChange,
    handleFileChange,
    handleCreateLawFirm,
    fuzzyMatchDialogOpen,
    setFuzzyMatchDialogOpen,
    fuzzyMatchResult,
    setFuzzyMatchResult,
    handleUseExistingLawFirm,
    handleCreateNewLawFirmAnyway,
    fuzzyMatchLocationDialogOpen,
    fuzzyMatchLocationResult,
    setFuzzyMatchLocationDialogOpen,
    setFuzzyMatchLocationResult,
    setPendingLocationSubmitData,
    handleUseExistingLocation,
    handleCreateNewLocationAnyway,
    locations,
    addressFieldErrors,
    handleLocationChange,
    handleGoogleCallback,
    handleZipCodeChange,
    allCities,
    allStates,
    allCounties,
    removeLocation,
    addLocation,
    handleCreateLocation,
    users,
    handleRemoveUser,
    handleUserInputChange,
    lawFirmLocations,
    positionOptions,
    lawSchools,
    legalServiceProfessionals,
    handleAddUser,
    handleSubmitAllUsers,
    setUsers,
    internalUsers,
  } = useCreateLawFirm();

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          <Typography
            variant="h4"
            gutterBottom
            style={{ textAlign: "center", fontWeight: "bold" }}
          >
            Create New Law Firm
          </Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel
            style={{ marginBottom: "20px" }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50px",
                marginBottom: "16px",
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {activeStep === 0 && (
            <Grid container spacing={2}>
              <LawFirmDetailsStep
                lawFirmData={lawFirmData}
                handleLawFirmChange={handleLawFirmChange}
                handleFileChange={handleFileChange}
                internalUsers={internalUsers}
              />
              <Grid size={{ xs: 12 }} style={{ textAlign: "right" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateLawFirm}
                >
                  CREATE LAW FIRM
                </Button>
              </Grid>
            </Grid>
          )}

          {activeStep === 1 && (
            <>
              <LocationsStep
                locations={locations}
                addressFieldErrors={addressFieldErrors}
                handleLocationChange={handleLocationChange}
                handleGoogleCallback={handleGoogleCallback}
                handleZipCodeChange={handleZipCodeChange}
                allCities={allCities}
                allStates={allStates}
                allCounties={allCounties}
                removeLocation={removeLocation}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "16px",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={addLocation}
                  startIcon={<AddIcon />}
                >
                  ADD ANOTHER LOCATION
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleCreateLocation}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: "white",
                      }}
                    />
                  ) : (
                    "Create Locations"
                  )}
                </Button>
              </Box>
            </>
          )}

          {activeStep === 2 && (
            <>
              <UsersStep
                users={users}
                handleRemoveUser={handleRemoveUser}
                handleUserInputChange={handleUserInputChange}
                lawFirmLocations={lawFirmLocations}
                positionOptions={positionOptions}
                lawSchools={lawSchools}
                legalServiceProfessionals={legalServiceProfessionals}
                setUsers={setUsers}
              />
              <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleAddUser}
                >
                  ADD ANOTHER USER
                </Button>
                <Box
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitAllUsers}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress size={24} />
                    ) : (
                      "Submit All Users"
                    )}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setUsers([
                        {
                          prefix: "",
                          firstName: "",
                          lastName: "",
                          middleName: "",
                          suffix: "",
                          email: "",
                          phoneNumber: "",
                          extension: "",
                          position: "",
                          permissionLevel: "User",
                          isLegalService: false,
                          isDefault: false,
                          lawschool: "",
                          barNumber: "",
                          is_gia_on_file: false,
                          gia_document_link: "",
                          gia_document_name: "",
                          gia_file: null,
                          selectedProfessional: "",
                          showCustomForm: false,
                          location: "",
                        },
                      ]);
                    }}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </>
          )}

          <LawFirmFuzzyMatchDialogBound
            open={fuzzyMatchDialogOpen}
            setOpen={setFuzzyMatchDialogOpen}
            fuzzyMatchResult={fuzzyMatchResult}
            setFuzzyMatchResult={setFuzzyMatchResult}
            onUseExisting={handleUseExistingLawFirm}
            onCreateAnyway={handleCreateNewLawFirmAnyway}
          />

          <Dialog
            open={fuzzyMatchLocationDialogOpen}
            onClose={() => {
              setFuzzyMatchLocationDialogOpen(false);
              setFuzzyMatchLocationResult(null);
              setPendingLocationSubmitData(null);
            }}
          >
            <DialogTitle>Similar location found</DialogTitle>
            <DialogContent>
              <Typography>
                A similar location already exists for this address
                {fuzzyMatchLocationResult?.addressSummary
                  ? `: ${fuzzyMatchLocationResult.addressSummary}`
                  : ""}
                {fuzzyMatchLocationResult?.score
                  ? ` (match score: ${Number(
                      fuzzyMatchLocationResult.score
                    ).toFixed(0)}%)`
                  : ""}
                . Do you want to use the existing location or create this new
                one anyway?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleUseExistingLocation} color="primary">
                Use existing
              </Button>
              <Button
                onClick={handleCreateNewLocationAnyway}
                color="secondary"
              >
                Create new anyway
              </Button>
            </DialogActions>
          </Dialog>
        </Paper>
      </Container>
    </>
  );
}

export default CreateLawFirm;
